import { useEffect, useState } from "react"
import { useHistory, useParams } from "react-router-dom"
import { useImport } from "web/hooks/data"
import {
  Button,
  ContentBox,
  IconButton,
  ImportErrors,
  Loading,
} from "web/components"
import Navigator from "web/components/Navigator/Navigator"
import TableData from "./TableData"
import Tabs from "./Tabs"

export default function ImportsShow({ backUrl, deprecatedTypes }) {
  const [refreshInterval, setRefreshInterval] = useState(false)
  const { id } = useParams()

  const importObj = useImport(id, { refreshInterval })

  const { doing_work: importDoingWork, sheets } = importObj
  const fileDownloadURL = `/imports/${importObj.id}/file`
  const canDelete = importObj.deletable

  const tabs = sheets.map((sheet) => ({
    name: sheet.name,
    count: sheet.row_count,
    status: sheet.status,
    key: sheet.id,
    hasErrors: sheet.error_count > 0,
    errorCount: sheet.error_count,
    props: ["importObj"],
  }))

  const shouldShowImportErrors = [
    "failed_on_processing",
    "failed_on_applying",
  ].includes(importObj.status)

  const importTitle = importObj.multiple_sheets_required
    ? `Import - ${importObj.filename}`
    : `${importObj.label} Import - ${importObj.filename}`

  useEffect(() => {
    setRefreshInterval(importDoingWork ? 2000 : 0)
  }, [importDoingWork])

  return (
    <div className="m-5 mt-3">
      <div className="mb-3">
        <Button
          variant="secondary_default"
          onClick={() => window.Turbo.visit(backUrl, { action: "replace" })}
        >
          &larr; Back to Import Data
        </Button>
      </div>
      <hr className="my-2 md:my-3" />

      {importDoingWork ? (
        <div className="mt-4">
          <Loading inline delay={0} />
        </div>
      ) : (
        <>
          <ContentBox>
            <a
              className="float-right"
              href={fileDownloadURL}
              data-turbo-frame="_top"
            >
              <IconButton size="small" outline name="cloud-arrow-down" />
            </a>
            <h2 className="text-2xl font-bold">{importTitle}</h2>

            {shouldShowImportErrors && (
              <ImportErrors errors={importObj.errors} />
            )}

            {importObj.multiple_sheets_required ? (
              <Navigator
                routes={tabs}
                component={TableData}
                childProps={{
                  importObj: importObj,
                }}
                render={({
                  routes,
                  selectedRouteKey,
                  onSelectRoute,
                  destinations,
                }) => (
                  <>
                    <Tabs
                      tabs={routes}
                      selectedTabKey={Number(selectedRouteKey)}
                      onTabSelect={(tab) => {
                        onSelectRoute([tab])
                      }}
                    />
                    {destinations}
                  </>
                )}
              ></Navigator>
            ) : (
              <TableData
                importObj={importObj}
                route={tabs}
                deprecatedTypes={deprecatedTypes}
              />
            )}
          </ContentBox>
          {canDelete && (
            <DeleteImport importObj={importObj} backUrl={backUrl} />
          )}
        </>
      )}
    </div>
  )
}

function DeleteImport({ importObj, backUrl }) {
  const history = useHistory()

  async function handleDelete() {
    await importObj.destroy()

    history.replace({
      pathname: backUrl,
      state: {
        value: `${importObj.type}`,
        label: `${importObj.label}`,
      },
    })
  }

  return (
    <div className="mt-2.5 rounded-md border border-gray-50 bg-white p-5 shadow-sm">
      <h3 className="text-lg font-bold">Delete {importObj.filename}</h3>
      <p className="mt-2 text-sm">
        We'll delete the file you uploaded. You can't undo this.
      </p>
      <div className="mt-3">
        <Button
          variant="primary_danger"
          disableAfterClick={true}
          onClick={handleDelete}
        >
          Delete
        </Button>
      </div>
    </div>
  )
}
